import React, { useEffect, useMemo, useState } from 'react';

import { FormattedMessage } from '../../util/reactIntl';
import { Modal } from '../../components';
import DataProcessingConsents from '../../components/DataProcessingConsents';

import css from './DataProcessingConsentPrompt.module.css';

const STORAGE_KEY_PREFIX = 'rematch:data-processing-prompt:v1';
const MODAL_ID = 'DataProcessingConsentPrompt';

const hasBrowserStorage = () => typeof window !== 'undefined' && !!window.localStorage;

const getStorageKey = userId => `${STORAGE_KEY_PREFIX}:${userId}`;

const hasPromptBeenShown = userId => {
  if (!userId || !hasBrowserStorage()) {
    return true;
  }

  try {
    return window.localStorage.getItem(getStorageKey(userId)) === 'shown';
  } catch (e) {
    return true;
  }
};

const markPromptShown = userId => {
  if (!userId || !hasBrowserStorage()) {
    return;
  }

  try {
    window.localStorage.setItem(getStorageKey(userId), 'shown');
  } catch (e) {
    // Storage can be unavailable in restricted browser modes. The consent API
    // remains the source of truth for saved choices.
  }
};

const DataProcessingConsentPrompt = props => {
  const {
    currentUser,
    isAuthenticated,
    isLoggedInAs,
    location,
    onManageDisableScrolling = () => null,
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const userId = currentUser?.id?.uuid;
  const isDataProcessingPage = location?.pathname === '/account/data-processing';

  const shouldPrompt = useMemo(() => {
    return (
      !!userId &&
      !!isAuthenticated &&
      !isLoggedInAs &&
      !isDataProcessingPage &&
      !hasPromptBeenShown(userId)
    );
  }, [isAuthenticated, isDataProcessingPage, isLoggedInAs, userId]);

  useEffect(() => {
    if (shouldPrompt) {
      setIsOpen(true);
    }
  }, [shouldPrompt]);

  const handleComplete = () => {
    markPromptShown(userId);
    setIsOpen(false);
  };

  if (!userId) {
    return null;
  }

  return (
    <Modal
      id={MODAL_ID}
      isOpen={isOpen}
      onClose={handleComplete}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      contentClassName={css.modalContent}
      closeButtonMessage={<FormattedMessage id="DataProcessingConsentPrompt.close" />}
    >
      <DataProcessingConsents
        className={css.consentPanel}
        source="SIGNUP"
        variant="prompt"
        onCompleted={handleComplete}
      />
      <p className={css.footnote}>
        <FormattedMessage id="DataProcessingConsentPrompt.footnote" />
      </p>
    </Modal>
  );
};

export default DataProcessingConsentPrompt;
